import { Suspense, lazy, useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { gtmPageBuilder } from '../helpers';
import { apiUrl } from '../services/api';
const FormCheckbox = lazy(() => import('./FormComponents/FormCheckbox'));
const FormHidden = lazy(() => import('./FormComponents/FormHidden'));
const FormServerError = lazy(() => import('./FormComponents/FormServerError'));
const FormSubmitButton = lazy(
  () => import('./FormComponents/FormSubmitButton')
);
const FormInput = lazy(() => import('./FormComponents/FormInput'));
const FormWithSsnData = lazy(() => import('./FormWithSsnData'));

interface Props {
  customerId?: string;
  headline?: string;
  text?: string;
  sendButtonText?: string;
  successHeadline?: string;
  successText?: string;
  leadsource?: string;
  officeId?: string;
  officeName?: string;
  regionId?: string;
}

const EvaluateResidenceForm = ({
  customerId,
  headline,
  text,
  sendButtonText,
  successHeadline,
  successText,
  leadsource,
  officeName,
  officeId,
  regionId,
}: Props): JSX.Element => {
  const form = useForm();
  const { isSubmitting, isSubmitSuccessful } = form.formState;
  const scrollElement = document.getElementById('evaluate-residence-form');

  const [selectedOfficeDropdown, setSelectedOfficeDropdown] =
    useState<string>();
  const [disableSubmit, setDisableSubmit] = useState(false);

  useEffect(() => {
    if (!customerId && (!regionId || regionId !== '0')) {
      setSelectedOfficeDropdown('LFF Huvudlicens');
    }
  }, []);

  return (
    <div
      id="evaluate-residence-form"
      className="flex overflow-y-auto overflow-x-hidden rounded-md py-10 px-3 xl:px-0"
    >
      <div
        className={`${
          isSubmitSuccessful
            ? 'translate-x-1.5/1- max-h-2/3 lg:max-h-screen/2'
            : ''
        } w-full flex-shrink-0 transform duration-300 mdplus:px-10 md:py-4`}
      >
        <div className=" max-w-2xl mx-auto lg:pb-4">
          <h3 className="text-blue700 text-center text-2xl md:text-2xl mb-4 lg:mb-6 lg:text-3xl">
            {headline || 'Värdera bostad'}
          </h3>
          {text && (
            <p className="w-full lg:text-lg max-w-lg mx-auto text-center mb-4 mt-1- lg:mt-2-">
              {text}
            </p>
          )}
          <Suspense>
            <FormWithSsnData
              form={form}
              novalidate={true}
              postUrl={apiUrl + '/api/Forms/EvaluateResidence'}
              setDisableSubmit={setDisableSubmit}
            >
              <FormInput
                placeholder="E-postadress"
                label="E-postadress*"
                name="EmailAddress"
                type="email"
                required
                pattern={/^\S+@\S+\.\S+$/i}
              />
              <FormInput
                placeholder="Telefonnummer"
                label="Telefonnummer*"
                name="phone"
                type="tel"
                pattern={/^(0|[+])([-\ ]?\d){7,12}$/i}
                required
              />
              <FormHidden
                name="LeadSourceId"
                value={leadsource || 'CMLEADTYPE4R308GBP5JC0MHPO'}
              />

              {customerId && !selectedOfficeDropdown && (
                <FormHidden name="OfficeCustomerId" value={customerId} />
              )}
              {!customerId && selectedOfficeDropdown && (
                <FormHidden name="OfficeCustomerId" value={'S13777'} />
              )}
              {officeId && <FormHidden name="OfficeId" value={officeId} />}
              <FormCheckbox
                label="<span class='text-formatting'>Jag har läst och tagit del av <a target='_blank' href='/integritetspolicy\'>integritetspolicyn</a>.</span>"
                name="Gdpr"
                required
              />
              <FormSubmitButton
                text={sendButtonText || 'Skicka'}
                isLoading={isSubmitting}
                disabled={disableSubmit}
              />
              <FormServerError formState={form.formState} />
            </FormWithSsnData>
          </Suspense>
        </div>
      </div>
      <div
        className={`${
          isSubmitSuccessful ? 'translate-x-1.5/1-' : ''
        } w-full flex-shrink-0 text-center transform duration-300 self-center ml-1/2`}
      >
        <>
          <svg
            className={`${
              isSubmitSuccessful ? 'checkmarksign' : 'checkmarksign'
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 52 52"
          >
            <circle
              className="checkmarksign__circle"
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <path
              className="checkmarksign__check"
              fill="none"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="max-w-md mx-auto">
              <p>{successHeadline || 'Tack!'}</p>
            </h2>
            <p className="max-w-md mx-auto">
              {successText || 'Vi återkopplar till dig så snart vi kan.'}
            </p>
          </div>
          {isSubmitSuccessful &&
            scrollElement &&
            scrollElement.scrollIntoView({ behavior: 'smooth' })}
          {isSubmitSuccessful && (
            <HelmetProvider>
              <Helmet>
                <script>
                  {`
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            'event': 'intresseformulär',
                            'eventInfo': {
                                'bobutik': '${
                                  officeName !== undefined ? officeName : ''
                                }',
                                'intressetyp': 'Värderingsförfrågan'
                                }
                        });

                        window.dataLayer.push({
                          'event': 'VirtualPageview',
                          'virtualPageURL': '${gtmPageBuilder(
                            officeName !== undefined ? officeName : ''
                          )}',
                          'virtualPageTitle': '${gtmPageBuilder(
                            officeName !== undefined ? officeName : ''
                          )}'
                      });
              `}
                </script>
              </Helmet>
            </HelmetProvider>
          )}
        </>
      </div>
    </div>
  );
};
export default EvaluateResidenceForm;
